<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { storeToRefs } from "pinia"
import { useForm } from "vee-validate"
import * as yup from "yup"
import { useToast } from "primevue/usetoast"
import { useI18n } from "vue-i18n"
import { useSourceStore } from "@core/stores/source.store"
import { useAuthStore } from "@core/stores/auth.store"
import { useSourceInfo } from "@core/composables/useSourceInfo"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import { formatDate, formatDateFr } from "@core/helpers"
import type { Source } from "@types"
// DEV-83 spike — banner "capteur inexistant" (cf. SensorInexistantBanner.vue)
import SensorInexistantBanner from "./SensorInexistantBanner.vue"
// DEV-255 spike — banner "modèle incompatible" (cf. SensorModelIncompatibleBanner.vue)
import SensorModelIncompatibleBanner from "./SensorModelIncompatibleBanner.vue"

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    /**
     * Identifiant de la source à afficher
     */
    sourceId: string
}

/**
 * Type pour un canal formaté
 */
type FormattedChannel = {
    name: string | null
    error: string | null
    typeName: string
    typeUnit: string
    type: string
    converter: string | null
}

// ================================
// PROPS / EMITS
// ================================
const props = defineProps<Props>()

/**
 * DEV-83 spike — réémit les events du SensorInexistantBanner au parent
 * (SensorInfoWrapper) qui orchestre le bascule mode lecture / mode édition.
 */
const emit = defineEmits<{
    (e: "edit-sensor"): void
    (e: "delete-sensor"): void
}>()

// ================================
// STORES & COMPOSABLES
// ================================
const sourceStore = useSourceStore()
const authStore = useAuthStore()
const toast = useToast()

const { getSourceById } = storeToRefs(sourceStore)
const {
    getVariables,
    getOperators,
    getSourceBrands,
    getOperator,
    getBrand,
    getModel,
    wifi,
} = useSourceInfo()

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            sensorInfo: {
                title: "Type de données récupérées",
                voie: "VOIE",
                name: "Nom",
                unit: "Unité de mesure",
                sensorName: "Nom du capteur",
                idsource: "Identifiant",
                idsourceInexistant: "Inexistant",
                idsourceInexistantTooltip:
                    "Cet identifiant n'est pas reconnu sur le réseau de l'opérateur",
                createdBy: "Ajouté par",
                createdAt: "Date d'ajout",
                owner: "Titulaire du capteur",
                broadcastFrequency: "Période d'émission",
                missingValues: "Données manquantes",
                lastMessage: "Dernier message reçu",
                lastRetrievalStatus: "Statut de la dernière récupération",
                lastDecodingStatus: "Statut dernier décodage",
                seconds: "secondes",
                none: "Aucune",
                noMessage: "Aucun",
                ok: "Ok",
                logo: "Logo",
            },
            buttons: {
                confirm: "Confirmer les données",
                saving: "Enregistrement...",
                error: "Erreur",
            },
            validation: {
                nameRequired: "Le nom est requis",
                nameMaxLength: "Le nom ne peut pas dépasser 255 caractères",
                nameNotEmpty: "Le nom ne peut pas être vide",
            },
            toast: {
                success: {
                    summary: "Succès",
                    detail: "Les informations du capteur ont été mises à jour",
                },
                error: {
                    summary: "Erreur",
                    detail: "Une erreur est survenue lors de la mise à jour",
                },
            },
        },
    },
})

// ================================
// ÉTAT DES DONNÉES
// ================================

/**
 * Nom initial du capteur (pour restauration en cas d'annulation)
 */
const initialName = ref<string>("")

/**
 * Affiche le libellé "Erreur" sur le bouton pendant 2s après une erreur
 */
const showErrorOnButton = ref(false)

// ================================
// MESSAGES TOAST
// ================================

const TOAST_MESSAGES = {
    success: {
        summary: t("toast.success.summary"),
        detail: t("toast.success.detail"),
        life: 5000,
    },
    error: {
        summary: t("toast.error.summary"),
        detail: t("toast.error.detail"),
        life: 5000,
    },
} as const

// ================================
// WRAPPER POUR LA SOUMISSION
// ================================

/**
 * useFetchWrapper pour la soumission du formulaire (update + refresh + toasts)
 */
const {
    fetch: submitFormWrapper,
    isFetching: isSubmitting,
    hasError: hasErrorSubmit,
    error: errorSubmit,
} = useFetchWrapper(
    async ({ name }: { name: string }) => {
        await sourceStore.updateSensor({
            _id_source: props.sourceId,
            sensorData: { name: name.trim() },
        })
        toast.add({
            severity: "success",
            ...TOAST_MESSAGES.success,
        })
        initialName.value = name.trim()
    },
    (err) => {
        toast.add({
            severity: "error",
            ...TOAST_MESSAGES.error,
            detail: err?.message ?? TOAST_MESSAGES.error.detail,
        })
        showErrorOnButton.value = true
        setTimeout(() => {
            showErrorOnButton.value = false
        }, 2000)
    }
)

// ================================
// DONNÉES CALCULÉES
// ================================

/**
 * Récupère la source depuis le store
 */
const getSource = computed<Source | undefined>(() => {
    return getSourceById.value(props.sourceId) as Source | undefined
})

/**
 * DEV-83 spike — Détecte si le capteur est marqué "inexistant" chez l'opérateur.
 * Lit `informations.iot.etat` exposé par dapi (DEV-79) depuis
 * `monitoring.statuscodelastretrieval == 404` (convention HOMEYS).
 */
const getIsInexistant = computed<boolean>(() => {
    return getSource.value?.informations?.iot?.etat === "inexistant"
})

/**
 * DEV-255 spike — Détecte si le modèle configuré pour le capteur est
 * incompatible avec les payloads reçus de l'opérateur. Lit
 * `informations.iot.etat` exposé par dapi (DEV-254) depuis
 * `monitoring.statuscodelastretrieval == 422` (convention HOMEYS DEV-247).
 */
const getIsModelIncompatible = computed<boolean>(() => {
    return getSource.value?.informations?.iot?.etat === "modele_incompatible"
})

/**
 * DEV-83 spike — Opérateur du capteur (passé au banner pour le wording).
 */
const getOperatorName = computed<string>(() => {
    return getSource.value?.operator || "l'opérateur"
})

/**
 * DEV-255 spike — Modèle configuré pour le capteur (passé au banner pour le wording).
 */
const getModelName = computed<string>(() => {
    return getSource.value?.details?.model || ""
})

/**
 * Récupère les logos de la marque, du modèle et de l'opérateur du capteur
 * @returns Liste des logos à afficher
 */
const getBrandModelAndOperatorImages = computed(() => {
    const options: Array<{ img?: string; name?: string }> = []
    if (!getSource.value) return options

    if (getOperators.value) {
        const operator = getOperator(getSource.value.operator || "")
        if (operator?.img) {
            options.push({ img: operator.img, name: operator.name })
        }
    }

    if (getSourceBrands.value && getSource.value.details?.brand) {
        const brand = getBrand(getSource.value.details.brand)
        if (brand?.img) {
            options.push({ img: brand.img, name: brand.name })
        }
    }

    // Ajout de l'image du modèle si disponible
    if (getSource.value.details?.model) {
        const model = getModel(getSource.value.details.model)
        if (model && model.img) {
            options.push({ img: model.img, name: model.name })
        }
    }

    return options
})

/**
 * Crée une map des variables indexée par type pour accès rapide
 * @returns Map des variables par type
 */
const getVariablesMap = computed(() => {
    if (!getVariables.value)
        return new Map<string, (typeof getVariables.value)[0]>()

    return new Map(getVariables.value.map((v) => [v.type, v]))
})

/**
 * Récupère la liste des canaux formatés avec leurs informations
 * @returns Liste des canaux formatés
 */
const getChannels = computed<FormattedChannel[]>(() => {
    if (!getSource.value?.channels || !getVariablesMap.value.size) return []

    return getSource.value.channels
        .map((channel) => {
            const variable = getVariablesMap.value.get(channel.type)
            if (!variable?.name) return null

            return {
                name: channel.name || null,
                error: channel.error ? channel.error_message : null,
                typeName: variable.name ?? "-",
                typeUnit: variable.unit ?? "-",
                type: channel.type,
                converter: channel.converter?.name || null,
            } as FormattedChannel
        })
        .filter((c): c is FormattedChannel => c !== null)
})

/**
 * Récupère la fréquence d'émission du capteur formatée avec espaces
 * @returns Fréquence formatée ou null
 */
const getBroadcastFrequency = computed(() => {
    return formatNumberWithSpaces(
        getSource.value?.monitoring?.broadcastfrequency
    )
})

/**
 * Récupère la date du dernier message reçu formatée
 * @returns Date formatée ou message par défaut
 */
const getLastDateMessage = computed(() => {
    if (!getSource.value?.monitoring?.datelastbroadcast)
        return t("sensorInfo.noMessage")
    return formatDateFr(getSource.value.monitoring.datelastbroadcast, {
        dateStyle: "long",
        timeStyle: "short",
    })
})

/**
 * Récupère le statut du dernier décodage
 * @returns Statut du décodage ou "Ok" par défaut
 */
const getLastAnalysisError = computed(() => {
    return getSource.value?.monitoring?.lastanalysiserror ?? t("sensorInfo.ok")
})

/**
 * Récupère le nombre de données manquantes formaté
 * @returns Nombre formaté ou "Aucune" si aucune donnée manquante
 */
const getMissingValues = computed(() => {
    const value = getSource.value?.monitoring?.missingvalues
    if (!value) return t("sensorInfo.none")
    return Number(parseFloat(String(value)).toFixed(3))
})

/**
 * Récupère le nom complet de la personne qui a créé le capteur
 * @returns Nom complet ou "-" si non disponible
 */
const getCreatedBy = computed(() => {
    if (!getSource.value?.createdBy) return "-"
    return `${getSource.value.createdBy.firstname} ${getSource.value.createdBy.lastname}`
})

/**
 * Récupère la date de création formatée
 * @returns Date formatée au format DD/MM/YYYY ou chaîne vide
 */
const getCreatedAt = computed(() => {
    if (!getSource.value?.createdAt) return ""
    return formatDate(
        getSource.value.createdAt,
        "DD/MM/YYYY",
        "",
        "Europe/Paris"
    )
})

/**
 * Récupère le statut wifi du capteur avec icône et couleur
 * @returns Objet contenant l'icône et la couleur du statut wifi
 */
const getWifiStatus = computed(() => {
    if (!getSource.value)
        return { icon: "pi pi-question-circle", color: "#6b7280" }
    return wifi(getSource.value)
})

/**
 * Vérifie si l'utilisateur a les permissions pour éditer le nom du capteur
 * @returns true si l'utilisateur peut éditer, false sinon
 */
const getCanEditName = computed(() => {
    return authStore.hasOfferEnergyManager && authStore.isEmployeWriteOrHigher
})

// ================================
// LOGIQUE MÉTIER
// ================================

/**
 * Formate un nombre en ajoutant des espaces tous les 3 chiffres
 * @param value - Valeur à formater
 * @returns Valeur formatée ou null
 */
function formatNumberWithSpaces(
    value: number | string | undefined
): string | null {
    if (!value) return null
    const parts = value.toString()
    if (!parts) return null

    // Inverse la chaîne, ajoute des espaces tous les 3 caractères, puis réinverse
    const reversed = parts.split("").reverse().join("")
    const matched = reversed.match(/.{1,3}/g)
    if (!matched) return parts

    return matched.join(" ").split("").reverse().join("")
}

// ================================
// FORMULAIRE & VALIDATION
// ================================

/**
 * Schéma de validation du formulaire de modification du nom
 */
const schema = yup.object({
    name: yup
        .string()
        .required(t("validation.nameRequired"))
        .trim()
        .max(255, t("validation.nameMaxLength"))
        .test("not-empty", t("validation.nameNotEmpty"), (value) => {
            return value !== undefined && value.trim() !== ""
        }),
})

const { errors, defineField, handleSubmit } = useForm({
    validationSchema: schema,
    initialValues: {
        name: getSource.value?.name || "",
    },
})

/**
 * Champ du formulaire pour le nom du capteur
 */
const [modelName] = defineField("name")

// ================================
// WATCHERS
// ================================

/**
 * Surveille les changements de la source et initialise le nom du formulaire
 */
watch(
    () => getSource.value,
    (newSource) => {
        if (newSource?.name) {
            modelName.value = newSource.name
            initialName.value = newSource.name
        }
    },
    { immediate: true, deep: true }
)

// ================================
// INTERACTIONS UTILISATEUR
// ================================

/**
 * Gère la soumission du formulaire de modification du nom via useFetchWrapper
 */
const onSubmit = handleSubmit(() => {
    if (!getSource.value) return
    submitFormWrapper({ name: modelName.value.trim() })
})
</script>

<template>
    <BaseCard class="sensor-info-display">
        <!-- ================================ -->
        <!-- HEADER -->
        <!-- ================================ -->
        <template #header>
            <div
                class="flex items-center justify-between border-b border-gray-200 p-6 bg-gray-50"
            >
                <h2 class="text-lg font-bold text-primary">
                    Informations du capteur
                </h2>
            </div>
        </template>

        <!-- ================================ -->
        <!-- CONTENU PRINCIPAL -->
        <!-- ================================ -->
        <template #content>
            <div class="flex flex-col gap-6 p-6">
                <!-- ================================ -->
                <!-- MARQUE & MODÈLE -->
                <!-- ================================ -->
                <div
                    class="flex flex-wrap items-center justify-center gap-5"
                    v-if="getBrandModelAndOperatorImages.length"
                >
                    <div
                        v-for="(item, index) in getBrandModelAndOperatorImages"
                        :key="`brand-model-${index}`"
                        class="flex items-center justify-center flex-wrap p-4 bg-white border border-gray-200 rounded-lg shadow-sm"
                    >
                        <img
                            :src="item.img"
                            :alt="item.name || t('sensorInfo.logo')"
                            class="h-20 w-20 object-contain"
                        />
                    </div>
                </div>
                <div
                    v-else
                    class="flex flex-wrap items-center justify-center gap-5"
                >
                    <BaseSkeleton
                        class="min-w-28 min-h-28 max-w-28 max-h-28"
                        v-for="index in 3"
                        :key="`skeleton-${index}`"
                    />
                </div>

                <!-- ================================ -->
                <!-- DEV-83 spike — BANNER "CAPTEUR INEXISTANT" -->
                <!-- Affiché quand informations.iot.etat === "inexistant" -->
                <!-- (mappé par dapi DEV-79 depuis monitoring.statuscodelastretrieval == 404) -->
                <!--
                    Events réémis au parent (SensorInfoWrapper) qui orchestre la
                    bascule mode lecture / mode édition (via le bouton "Modifier
                    les informations" du header existant). Le banner devient un
                    2e point d'entrée vers le même parcours XState.
                -->
                <!-- ================================ -->
                <SensorInexistantBanner
                    v-if="getIsInexistant"
                    :operator="getOperatorName"
                    :sensor-name="getSource?.name || ''"
                    @edit-sensor="emit('edit-sensor')"
                    @delete-sensor="emit('delete-sensor')"
                />

                <!-- ================================ -->
                <!-- DEV-255 spike — BANNER "MODÈLE INCOMPATIBLE" -->
                <!-- Affiché quand informations.iot.etat === "modele_incompatible" -->
                <!-- (mappé par dapi DEV-254 depuis monitoring.statuscodelastretrieval == 422, -->
                <!--  cf. data DEV-247 BadModelError → SUCCESS métier) -->
                <!--
                    L'event `edit-sensor` est réémis au parent qui ouvre
                    SourceAppendDialog en mode édition — l'utilisateur peut
                    re-sélectionner un modèle compatible avec les payloads.
                -->
                <!-- ================================ -->
                <SensorModelIncompatibleBanner
                    v-if="getIsModelIncompatible"
                    :operator="getOperatorName"
                    :model="getModelName"
                    @edit-sensor="emit('edit-sensor')"
                />

                <!-- ================================ -->
                <!-- CANAUX -->
                <!-- ================================ -->
                <div
                    v-if="getChannels.length > 0"
                    class="border-b border-gray-200 pb-6"
                >
                    <h4 class="text-lg mb-6 text-primary">
                        {{ t("sensorInfo.title") }}
                    </h4>
                    <div class="flex flex-col gap-4">
                        <div
                            v-for="(channel, index) in getChannels"
                            :key="`channel-${index}`"
                            class="flex flex-col gap-2"
                        >
                            <div class="flex items-center gap-4">
                                <BaseBadge
                                    :value="`${t('sensorInfo.voie')} ${index + 1}`"
                                    severity="primary"
                                />
                                <div class="flex flex-col">
                                    <span
                                        class="font-semibold text-base text-primary"
                                    >
                                        {{ channel.typeName }}
                                    </span>
                                    <span
                                        v-if="channel.typeUnit"
                                        class="text-sm text-primary"
                                    >
                                        {{ t("sensorInfo.unit") }} :
                                        <span class="font-semibold">{{
                                            channel.converter ||
                                            channel.typeUnit
                                        }}</span>
                                    </span>
                                    <div v-if="channel.name" class="text-sm">
                                        <span class="text-primary"
                                            >{{ t("sensorInfo.name") }} :</span
                                        >
                                        <span
                                            class="ml-2 font-semibold text-primary"
                                        >
                                            {{ channel.name }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <BaseMessage
                                v-if="channel.error"
                                severity="error"
                                variant="simple"
                                size="small"
                            >
                                {{ channel.error }}
                            </BaseMessage>
                        </div>
                    </div>
                </div>

                <!-- ================================ -->
                <!-- PROPRIÉTAIRE -->
                <!-- ================================ -->
                <div class="border-b border-gray-200 pb-6">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <!-- Nom du capteur -->
                        <BaseInputWrapper
                            v-if="getCanEditName"
                            input-id="sensor-name"
                            :label="t('sensorInfo.sensorName')"
                            :error="errors.name"
                        >
                            <BaseInputText
                                v-model="modelName"
                                input-id="sensor-name"
                                :invalid="!!errors.name"
                            />
                        </BaseInputWrapper>
                        <div v-else class="flex flex-col gap-2">
                            <label class="text-sm text-primary">
                                {{ t("sensorInfo.sensorName") }}
                            </label>
                            <p class="font-semibold text-primary">
                                {{ getSource?.name || "-" }}
                            </p>
                        </div>

                        <!--
                            Identifiant (DEV-83 spike — affichage global)
                            Affiché systématiquement (pas seulement quand inexistant).
                            L'idsource est le "PDL" du capteur IoT, doit être visible/copiable.
                            Style aligné sur les autres champs (h5 light + p semibold + text-primary).

                            DEV-83 retour user : badge "Inexistant" affiché juste à
                            côté de l'identifiant quand l'opérateur ne le reconnaît pas
                            (au lieu d'être en haut du banner). Cohérence visuelle :
                            l'info "cet identifiant n'est pas reconnu" se lit AU NIVEAU
                            de l'identifiant lui-même.
                        -->
                        <div class="flex flex-col gap-2">
                            <h5
                                class="text-sm font-light text-primary"
                                :title="t('sensorInfo.idsource')"
                            >
                                {{ t("sensorInfo.idsource") }}
                            </h5>
                            <div class="flex items-center gap-2 flex-wrap">
                                <p
                                    class="font-semibold text-primary select-all m-0"
                                    :title="getSource?.idsource || ''"
                                >
                                    {{ getSource?.idsource || "-" }}
                                </p>
                                <BaseTag
                                    v-if="getIsInexistant"
                                    severity="danger"
                                    icon="pi pi-exclamation-triangle"
                                    :value="t('sensorInfo.idsourceInexistant')"
                                    v-tooltip.top="
                                        t(
                                            'sensorInfo.idsourceInexistantTooltip'
                                        )
                                    "
                                    aria-label="Cet identifiant n'est pas reconnu chez l'opérateur"
                                />
                            </div>
                        </div>

                        <!-- Créé par -->
                        <div class="flex flex-col gap-2">
                            <h5
                                class="text-sm font-light text-primary"
                                :title="t('sensorInfo.createdBy')"
                            >
                                {{ t("sensorInfo.createdBy") }}
                            </h5>
                            <p
                                class="font-semibold text-primary"
                                :title="getCreatedBy"
                            >
                                {{ getCreatedBy }}
                            </p>
                        </div>

                        <!-- Date d'ajout -->
                        <div class="flex flex-col gap-2">
                            <h5
                                class="text-sm font-light text-primary"
                                :title="t('sensorInfo.createdAt')"
                            >
                                {{ t("sensorInfo.createdAt") }}
                            </h5>
                            <p
                                class="font-semibold text-primary"
                                :title="getCreatedAt"
                            >
                                {{ getCreatedAt }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- ================================ -->
                <!-- TITULAIRE -->
                <!-- ================================ -->
                <div
                    v-if="getSource?.owner"
                    class="border-b border-gray-200 pb-6"
                >
                    <div class="flex flex-col gap-2">
                        <h5 class="text-sm font-light text-primary">
                            {{ t("sensorInfo.owner") }}
                        </h5>
                        <div v-if="getSource.owner.relation_type === 'user'">
                            <p class="font-semibold text-primary">
                                {{ getSource.owner.firstname }}
                                {{ getSource.owner.lastname }}
                            </p>
                            <p
                                v-if="getSource.owner.username"
                                class="text-sm text-primary"
                            >
                                {{ getSource.owner.username }}
                            </p>
                        </div>
                        <div
                            v-else-if="
                                getSource.owner.relation_type === 'company'
                            "
                        >
                            <p class="font-semibold text-primary">
                                {{ getSource.owner.name }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- ================================ -->
                <!-- MONITORING -->
                <!-- ================================ -->
                <div
                    class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 border-b border-gray-200 pb-6"
                >
                    <!-- Période d'émission -->
                    <div class="flex flex-col gap-2">
                        <h5 class="text-sm font-light text-primary">
                            {{ t("sensorInfo.broadcastFrequency") }}
                        </h5>
                        <p
                            v-if="getBroadcastFrequency"
                            class="font-semibold text-primary"
                        >
                            {{ getBroadcastFrequency }}
                            {{ t("sensorInfo.seconds") }}
                        </p>
                        <p v-else class="font-semibold text-primary">-</p>
                    </div>

                    <!-- Données manquantes -->
                    <div class="flex flex-col gap-2">
                        <h5 class="text-sm font-light text-primary">
                            {{ t("sensorInfo.missingValues") }}
                        </h5>
                        <p
                            class="font-semibold text-primary flex items-center gap-2"
                            :title="String(getMissingValues)"
                        >
                            {{ getMissingValues }}
                            <BaseIcon
                                v-if="getMissingValues === t('sensorInfo.none')"
                                icon="pi pi-check"
                                size="13px"
                                color="#22c55e"
                            />
                        </p>
                    </div>

                    <!-- Dernier message reçu -->
                    <div class="flex flex-col gap-2">
                        <h5
                            class="text-sm font-light text-primary"
                            :title="t('sensorInfo.lastMessage')"
                        >
                            {{ t("sensorInfo.lastMessage") }}
                        </h5>
                        <p
                            class="font-semibold text-primary"
                            :title="getLastDateMessage"
                        >
                            {{ getLastDateMessage }}
                        </p>
                    </div>

                    <!-- Statut dernière récupération -->
                    <div class="flex flex-col gap-2">
                        <h5
                            class="text-sm font-light text-primary"
                            :title="t('sensorInfo.lastRetrievalStatus')"
                        >
                            {{ t("sensorInfo.lastRetrievalStatus") }}
                        </h5>
                        <p>
                            <BaseIcon
                                :icon="getWifiStatus.icon"
                                :style="{ color: getWifiStatus.color }"
                                size="20px"
                            />
                        </p>
                    </div>

                    <!-- Statut dernier décodage -->
                    <div class="flex flex-col gap-2">
                        <h5
                            class="text-sm font-light text-primary"
                            :title="t('sensorInfo.lastDecodingStatus')"
                        >
                            {{ t("sensorInfo.lastDecodingStatus") }}
                        </h5>
                        <p
                            class="font-semibold text-primary flex items-center gap-2"
                            :title="getLastAnalysisError"
                        >
                            {{ getLastAnalysisError }}
                            <BaseIcon
                                v-if="
                                    getLastAnalysisError === t('sensorInfo.ok')
                                "
                                icon="pi pi-check"
                                size="13px"
                                color="#22c55e"
                            />
                        </p>
                    </div>
                </div>

                <!-- ================================ -->
                <!-- MESSAGES D'ÉTAT -->
                <!-- ================================ -->

                <BaseMessage severity="error" v-if="hasErrorSubmit">
                    {{ errorSubmit?.message || t("toast.error.detail") }}
                </BaseMessage>

                <!-- ================================ -->
                <!-- BOUTONS D'ACTION -->
                <!-- ================================ -->
                <BaseButton
                    v-if="getCanEditName"
                    :label="
                        isSubmitting
                            ? t('buttons.saving')
                            : showErrorOnButton || hasErrorSubmit
                              ? t('buttons.error')
                              : t('buttons.confirm')
                    "
                    :loading="isSubmitting"
                    :severity="
                        showErrorOnButton || hasErrorSubmit
                            ? 'danger'
                            : 'primary'
                    "
                    @click="onSubmit"
                    :disabled="isSubmitting"
                    class="w-fit mx-auto"
                />
            </div>
        </template>
    </BaseCard>
</template>
